<template>
    <vx-modal v-model:visible="visible" size="lg" :padding="false">
        <template v-slot:header>
            <div class="pt-6 px-6">Review Billing Configuration</div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">{{ team.name }} - {{ actionLabel }}</div>
        </template>

        <div class="p-6 space-y-6">
            <!-- Section 1: Payment Method & Terms (only for activate) -->
            <kiosk-team-payment-terms-card
                v-if="action === 'activate' && team"
                :team="team"
                :team-id="team.id"
                @updated="handlePaymentTermsUpdated"
            />

            <!-- Section 2: Pricing & Commitments -->
            <kiosk-team-pricing-card v-if="team" :team="team" :team-id="team.id" @updated="pricingSaved = true" />

            <!-- Section 3: Final Action -->
            <vx-expandable-card>
                <template v-slot:header>
                    <span>{{ action === 'activate' ? 'Activate Team' : 'Suspend Team' }}</span>
                </template>
                <template v-slot:content>
                    <div class="flex flex-col gap-6 mt-3">
                        <!-- For suspend: reason input -->
                        <div v-if="action === 'suspend'" class="space-y-4">
                            <vx-label size="md">Why is the team being suspended?</vx-label>
                            <vx-radio-selection size="sm" v-model="selectedReason">
                                <vx-radio-selection-item value="Invalid Payment"
                                    >Invalid Payment</vx-radio-selection-item
                                >
                                <vx-radio-selection-item value="Churn">Churn</vx-radio-selection-item>
                                <vx-radio-selection-item value="custom">Custom</vx-radio-selection-item>
                            </vx-radio-selection>

                            <div v-if="selectedReason === 'custom'" class="mt-4">
                                <vx-label size="md">Custom Reason</vx-label>
                                <vx-input
                                    v-model="reason"
                                    size="lg"
                                    :rows="4"
                                    maxlength="250"
                                    data-test="suspend-reason-input"
                                ></vx-input>
                            </div>
                        </div>

                        <!-- Final action button -->
                        <vx-button
                            @click="handleFinalAction"
                            :loading="submitting"
                            :disabled="action === 'suspend' && !reason"
                            size="lg"
                            :color="action === 'activate' ? 'primary' : 'danger'"
                            class="w-full"
                            data-test="final-action-button"
                        >
                            {{ action === 'activate' ? 'Activate Team' : 'Suspend Team' }}
                        </vx-button>

                        <!-- Informational text -->
                        <div class="text-xs text-gray-500 text-center">
                            {{ actionText }}
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </vx-modal>
</template>

<script setup>
import {
    VxButton,
    VxModal,
    VxExpandableCard,
    VxInput,
    VxLabel,
    VxRadioSelection,
    VxRadioSelectionItem,
} from '@voxie/frontend-components';
import { ref, computed, watch } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient } from '~/services';
import KioskTeamPaymentTermsCard from '~/components/kiosk/kiosk-teams/cards/KioskTeamPaymentTermsCard.vue';
import KioskTeamPricingCard from '~/components/kiosk/kiosk-teams/cards/KioskTeamPricingCard.vue';

const props = defineProps({
    action: {
        type: String,
        required: true,
    },
});

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const team = defineModel('team', {
    type: Object,
    required: true,
});

const toasted = useToasted();

// Section 3 State (Final Action)
const selectedReason = ref('');
const reason = ref('');
const submitting = ref(false);

// Optional: Track saved status for future features
const paymentTermsSaved = ref(false);
const pricingSaved = ref(false);

// Computed
const actionLabel = computed(() => {
    if (props.action === 'activate') {
        return team.value.status === 'suspended' ? 'Re-Activate' : 'Activate';
    }
    return 'Suspend';
});

const actionText = computed(() => {
    if (props.action === 'activate') {
        if (team.value.status === 'suspended') {
            return 'Team members will regain full access and automated messaging will resume.';
        }

        return 'The team will be activated with the above billing configuration.';
    }

    return 'Team members will have read-only access and all outbound messaging will be stopped.';
});

// Handle payment terms update (optimistic update for modal)
const handlePaymentTermsUpdated = (data) => {
    team.value.is_billable = data.is_billable;
    team.value.payment_terms_days = data.payment_terms_days;
    paymentTermsSaved.value = true;
};

// Watch selected reason for suspend
watch(selectedReason, () => {
    reason.value = selectedReason.value === 'custom' ? '' : selectedReason.value;
});

// Reset state when modal closes
watch(visible, (newValue) => {
    if (!newValue) {
        selectedReason.value = '';
        reason.value = '';
        paymentTermsSaved.value = false;
        pricingSaved.value = false;
    }
});

// Final action handler
const handleFinalAction = async () => {
    if (submitting.value) {
        return;
    }

    if (props.action === 'suspend' && !reason.value) {
        return;
    }

    submitting.value = true;

    try {
        if (props.action === 'activate') {
            await adminTeamsClient.activateTeam(team.value.id);
            team.value.status = 'active';
            team.value.status_reason = '';
            toasted.success('Team activated successfully');
        } else {
            await adminTeamsClient.suspendTeam(team.value.id, {
                reason: reason.value,
            });
            team.value.status = 'suspended';
            team.value.status_reason = reason.value;
            toasted.success('Team suspended successfully');
        }

        visible.value = false;

        // Reset state
        paymentTermsSaved.value = false;
        pricingSaved.value = false;
        selectedReason.value = '';
        reason.value = '';
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }
        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        submitting.value = false;
    }
};
</script>
